import React from 'react';
import Icon from '../../../components/AppIcon';

const ValidationIndicators = ({ sections, completionPercentage, onSectionClick }) => {
  const incompleteSections = sections?.filter(section => !section?.validation?.complete) || [];
  const completeSections = sections?.filter(section => section?.validation?.complete) || [];
  
  const getQualityScore = () => {
    if (completionPercentage >= 90) return { label: 'Excellent', color: 'text-green-600', bgColor: 'bg-green-50 border-green-200' };
    if (completionPercentage >= 70) return { label: 'Good', color: 'text-blue-600', bgColor: 'bg-blue-50 border-blue-200' };
    if (completionPercentage >= 50) return { label: 'Fair', color: 'text-yellow-600', bgColor: 'bg-yellow-50 border-yellow-200' };
    return { label: 'Needs Work', color: 'text-red-600', bgColor: 'bg-red-50 border-red-200' };
  };

  const qualityScore = getQualityScore();

  const recommendations = [
    ...(incompleteSections?.includes(sections?.find(s => s?.key === 'profile')) ? ['Complete basic profile information'] : []),
    ...(incompleteSections?.includes(sections?.find(s => s?.key === 'promotion')) ? ['Upload promotional materials (splash screen, logo)'] : []),
    ...(incompleteSections?.includes(sections?.find(s => s?.key === 'about')) ? ['Add artist biography and about text'] : []),
    ...(incompleteSections?.includes(sections?.find(s => s?.key === 'work')) ? ['Add portfolio works to showcase talent'] : []),
    ...(incompleteSections?.includes(sections?.find(s => s?.key === 'calendar')) ? ['Schedule upcoming events or performances'] : []),
    ...(completionPercentage < 100 ? ['Complete all sections for maximum visibility'] : [])
  ]?.slice(0, 3);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* Content Quality Score */}
      <div className={`border rounded-lg p-4 ${qualityScore?.bgColor}`}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
            <Icon name="Award" className={qualityScore?.color} size={20} />
          </div>
          <div>
            <h4 className="font-medium text-text-primary">Content Quality</h4>
            <p className={`text-sm font-medium ${qualityScore?.color}`}>
              {qualityScore?.label} ({completionPercentage}%)
            </p>
          </div>
        </div>
      </div>

      {/* Completion Status */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
            <Icon name="CheckCircle" className="text-primary" size={20} />
          </div>
          <div>
            <h4 className="font-medium text-text-primary">Sections Complete</h4>
            <p className="text-sm text-text-secondary">
              {completeSections?.length} of {sections?.length} sections
            </p>
          </div>
        </div>
      </div>

      {/* Missing Content */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
            <Icon name="AlertCircle" className="text-orange-500" size={20} />
          </div>
          <div>
            <h4 className="font-medium text-text-primary">Missing Content</h4>
            <p className="text-sm text-text-secondary">
              {incompleteSections?.length} section{incompleteSections?.length !== 1 ? 's' : ''} incomplete
            </p>
          </div>
        </div>
      </div>

      {/* Recommendations */}
      {recommendations?.length > 0 && (
        <div className="lg:col-span-3 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Icon name="Lightbulb" className="text-blue-600 mt-1 flex-shrink-0" size={20} />
            <div className="flex-1">
              <h4 className="font-medium text-blue-900 mb-2">Recommendations to Improve Profile</h4>
              <ul className="space-y-1 text-sm text-blue-800">
                {recommendations?.map((rec, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <Icon name="ArrowRight" size={14} className="mt-0.5 flex-shrink-0" />
                    <span>{rec}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Section Status Grid */}
      <div className="lg:col-span-3">
        <h4 className="font-medium text-text-primary mb-3">Section Status Overview</h4>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {sections?.map((section) => (
            <div 
              key={section?.key} 
              className="bg-card border border-border rounded-lg p-3 text-center cursor-pointer hover:bg-primary/5 hover:border-primary/20 transition-all duration-200 group"
              onClick={() => onSectionClick?.(section?.key)}
              title={`Click to edit ${section?.title}`}
            >
              <div className={`w-8 h-8 mx-auto rounded-full flex items-center justify-center mb-2 transition-colors group-hover:scale-110 ${
                section?.validation?.complete 
                  ? 'bg-green-100 text-green-600 group-hover:bg-green-200' :'bg-gray-100 text-gray-400 group-hover:bg-primary/10 group-hover:text-primary'
              }`}>
                <Icon name={section?.icon} size={16} />
              </div>
              <p className="text-xs font-medium text-text-primary mb-1 group-hover:text-primary transition-colors">
                {section?.title}
              </p>
              <div className={`w-2 h-2 rounded-full mx-auto transition-all group-hover:scale-125 ${
                section?.validation?.complete ? 'bg-green-500' : 'bg-gray-300 group-hover:bg-primary/60'
              }`} />
              
              {/* Edit hint on hover */}
              <div className="opacity-0 group-hover:opacity-100 transition-opacity mt-1">
                <Icon name="Edit" size={12} className="text-primary mx-auto" />
              </div>
            </div>
          ))}
        </div>
        <p className="text-xs text-text-secondary mt-2 opacity-75">
          💡 Click any section above to edit its content
        </p>
      </div>
    </div>
  );
};

export default ValidationIndicators;